/* eslint-disable sonarjs/no-duplicate-string */
import React, { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { getFormattedFiatCurrencyNumber } from '@utils';

import { useStore } from '@modules';
import { EPaymentStatuses, EUserStatuses } from '@modules/transactions/types';
import { TTransaction } from '@modules/transactions/types2';

import CopyText from '@containers/CopyText';

const getShort = (string?: string) => {
  if (!string) return '';
  if (string.length <= 10) return string;
  return `${string.slice(0, 5)}...${string.slice(-4)}`;
};

const BlockchainIdLink: CustomFC<{ url: string; id: string }> = ({ url, id }) => (
  <a className="link" href={url} target="_blank" rel="noreferrer" title={id}>
    {getShort(id)}
  </a>
);

const getStatus = (status: EPaymentStatuses): EUserStatuses => {
  switch (status) {
    case EPaymentStatuses.paid:
    case EPaymentStatuses.completed:
    case EPaymentStatuses.succeeded:
    case EPaymentStatuses.sent:
      return EUserStatuses.success;

    case EPaymentStatuses.failed:
    case EPaymentStatuses.order_failed:
    case EPaymentStatuses.descriptor_failed:
    case EPaymentStatuses.failed_exchange:
    case EPaymentStatuses.failed_payout:
    case EPaymentStatuses.limits_exceeded:
    case EPaymentStatuses.failed_send:
      return EUserStatuses.failed;

    case EPaymentStatuses.cancelled:
      return EUserStatuses.cancelled;

    default:
      return EUserStatuses.pending;
  }
};

export type RowData = {
  status: EUserStatuses;
  timestamp: number;
  info: ReactNode;
  crypto: `${'+' | '-'}${string}`;
  fiat: string | false;
  details: {
    account?: [ReactNode, ReactNode][];
    content?: [ReactNode, ReactNode][];
    add: [ReactNode, ReactNode][];
  };
};

const useRowData = (data: TTransaction): RowData => {
  const { t } = useTranslation();

  const { type, currency, amount, fiat_currency, fiat_amount, id, created_at_ts } = data;

  const [{ global }] = useStore();
  const { currencies } = global;
  const currencyFullname = currencies.config.display_options[currency]?.fullname || currency;
  const status = getStatus(data.status);

  const getFiat = (fiatAmount: string, showZero = false) =>
    (!!+fiatAmount || showZero) &&
    !!fiat_currency &&
    getFormattedFiatCurrencyNumber({ amount: +fiatAmount, currency: fiat_currency });

  const fiat = getFiat(fiat_amount);
  const crypto = `${amount} ${currency}`;

  if (type === 'buy') {
    return {
      status,
      timestamp: created_at_ts,
      info: `${data.card?.payment_system || ''} · ${
        data.card?.number?.slice(-4) || ''
      } → ${currencyFullname}`,
      crypto: `+${crypto}`,
      fiat,
      details: {
        content: [
          [t('rate-info.rate'), getFiat(data.rate_history?.[0]?.reverse_rate)],
          [`${crypto} =`, getFiat(data.calculated_amount)],
          [t('rate-info.fee'), getFiat(data.fee)],
          [t('rate-info.total'), fiat],
        ],
        add: [[t('transaction.mercuryo_id'), <CopyText text={id} title={id} />]],
      },
    };
  }

  if (type === 'sell') {
    return {
      status,
      timestamp: created_at_ts,
      info: `${currencyFullname} → ${data.card?.payment_system || ''} · ${
        data.card?.number?.slice(-4) || ''
      }`,
      crypto: `-${crypto}`,
      fiat: getFiat(data.calculated_amount),
      details: {
        content: [
          [t('rate-info.rate'), getFiat(data.rate_history?.[0]?.rate)],
          [`${crypto} =`, getFiat(data.calculated_amount)],
          [t('rate-info.fee'), getFiat(data.fee)],
          [t('rate-info.total'), crypto],
        ],
        add: [[t('transaction.mercuryo_id'), <CopyText text={id} title={id} />]],
      },
    };
  }

  if (type === 'deposit') {
    const address = data.is_offchain ? data.trace.address : data.tx.address;
    const shortAddress = getShort(address) || currencyFullname;
    return {
      status,
      timestamp: created_at_ts,
      info: `${shortAddress} → ${currencyFullname}`,
      crypto: `+${crypto}`,
      fiat,
      details: {
        add: [
          [
            t('transactions.from'),
            <CopyText text={address} title={address}>
              {shortAddress}
            </CopyText>,
          ],
          [t('transaction.mercuryo_id'), <CopyText text={id} title={id} />],
          [
            t('transaction.blockchain_id'),
            !data.is_offchain && data.tx.id && (
              <BlockchainIdLink url={data.tx.url} id={data.tx.id} />
            ),
          ],
        ],
      },
    };
  }

  if (type === 'withdraw') {
    const address = data.is_offchain ? data.trace.address : data.tx.address;
    const shortAddress = getShort(address) || currencyFullname;
    return {
      status,
      timestamp: created_at_ts,
      info: `${currencyFullname} → ${shortAddress}`,
      crypto: `-${crypto}`,
      fiat,
      details: {
        content: [
          [
            t('transactions.to'),
            <CopyText text={address} title={address}>
              {shortAddress}
            </CopyText>,
          ],
          [
            t('transaction.amount'),
            data.calculated_amount ? `${data.calculated_amount} ${currency}` : '',
          ],
          [t('transaction.blockchain_fee'), `${data.fee} ${data.fee_currency}`],
          [t('rate-info.total'), crypto],
        ],
        add: [
          [t('transaction.mercuryo_id'), <CopyText text={id} title={id} />],
          [
            t('transaction.blockchain_id'),
            !data.is_offchain && data.tx.id && (
              <BlockchainIdLink url={data.tx.url} id={data.tx.id} />
            ),
          ],
        ],
      },
    };
  }

  if (type === 'mobile_pay') {
    const pay_system = data.mobile_pay.pay_system === 'apple' ? 'Apple Pay' : 'Google Pay';
    return {
      status,
      timestamp: created_at_ts,
      info: `${pay_system} → ${currencyFullname}`,
      crypto: `+${crypto}`,
      fiat,
      details: {
        content: [
          [t('rate-info.rate'), getFiat(data.rate_history?.[0]?.reverse_rate)],
          [`${crypto} =`, getFiat(data.calculated_amount)],
          [t('rate-info.fee'), getFiat(data.fee)],
          [t('rate-info.total'), fiat],
        ],
        add: [[t('transaction.mercuryo_id'), <CopyText text={id} title={id} />]],
      },
    };
  }

  if (type === 'buy_acquirer') {
    return {
      status,
      timestamp: created_at_ts,
      info: currencyFullname,
      crypto: `+${crypto}`,
      fiat,
      details: {
        content: [
          [`${crypto} =`, getFiat(data.calculated_amount)],
          [t('rate-info.fee'), getFiat(data.fee)],
          [t('rate-info.total'), fiat],
        ],
        add: [[t('transaction.mercuryo_id'), <CopyText text={id} title={id} />]],
      },
    };
  }

  if (type === 'fiat_deposit') {
    return {
      status,
      timestamp: created_at_ts,
      info: `${t('iban.top_menu.bank_account')} → ${CONST.accountsCurrency}`,
      crypto: `+${fiat_amount} ${fiat_currency}`,
      fiat: false,
      details: {
        content: [
          [t('transaction.amount'), getFiat(data.calculated_amount)],
          [t('rate-info.fee'), getFiat(data.tx.fee, true)],
          [t('rate-info.total'), fiat],
        ],
        add: [[t('transaction.mercuryo_id'), <CopyText text={id} title={id} />]],
      },
    };
  }

  if (type === 'fiat_buy') {
    return {
      status,
      timestamp: created_at_ts,
      info: `${CONST.accountsCurrency} → ${currencyFullname}`,
      crypto: `+${crypto}`,
      fiat,
      details: {
        content: [
          [`${crypto} =`, getFiat(data.calculated_amount)],
          [t('transaction.blockchain_fee'), getFiat(data.tx.fiat_fee)],
          [t('rate-info.total'), fiat],
        ],
        add: [[t('transaction.mercuryo_id'), <CopyText text={id} title={id} />]],
      },
    };
  }

  if (type === 'fiat_sell') {
    return {
      status,
      timestamp: created_at_ts,
      info: `${CONST.accountsCurrency} → ${currencyFullname}`,
      crypto: `-${crypto}`,
      fiat,
      details: {
        content: [
          [`${crypto} =`, getFiat(data.calculated_amount)],
          [t('transaction.blockchain_fee'), getFiat(data.tx.fee)],
          [t('rate-info.total'), fiat],
        ],
        add: [[t('transaction.mercuryo_id'), <CopyText text={id} title={id} />]],
      },
    };
  }

  if (type === 'fiat_withdraw') {
    return {
      status,
      timestamp: created_at_ts,
      info: `${CONST.accountsCurrency} → ${t('iban.top_menu.bank_account')}`,
      crypto: `-${fiat_amount} ${fiat_currency}`,
      fiat: false,
      details: {
        account: [
          [t('public.iban'), <CopyText text={data.tx.iban} title={data.tx.iban} />],
          [t('sepa.recipient'), data.tx.name],
          [
            t('sepa.recipient_country'),
            !!data.tx.country &&
              new Intl.DisplayNames(['en'], { type: 'region' }).of(data.tx.country),
          ],
        ],
        content: [
          [t('transaction.amount'), getFiat(data.calculated_amount)],
          [t('transaction.fee'), getFiat(data.tx.fee, true)],
          [t('rate-info.total'), fiat],
        ],
        add: [[t('transaction.mercuryo_id'), <CopyText text={id} title={id} />]],
      },
    };
  }

  if (['iban_charge', 'issue'].includes(type)) {
    return {
      status,
      timestamp: created_at_ts,
      info: currencyFullname,
      crypto: `-${crypto}`,
      fiat: currency !== fiat_currency && fiat,
      details: {
        add: [[t('transaction.mercuryo_id'), <CopyText text={id} title={id} />]],
      },
    };
  }

  return {
    status,
    timestamp: created_at_ts,
    info: currencyFullname,
    crypto: `+${crypto}`,
    fiat: currency !== fiat_currency && fiat,
    details: {
      add: [[t('transaction.mercuryo_id'), <CopyText text={id} title={id} />]],
    },
  };
};

export default useRowData;
